import { DateTime } from 'luxon';
import { toIANATimezone } from './time-utils.js';
/**
 * Default timezone for business hours
 */
export const DEFAULT_TIMEZONE = 'AMERICA_NEW_YORK';
/**
 * Default timezone name in IANA format for business hours
 */
export const DEFAULT_TIMEZONE_NAME = 'America/New_York';
/**
 * Default weekly schedule for business hours
 */
export const DEFAULT_WEEKLY_SCHEDULE = {
    monday: { enabled: true, timeRanges: [{ from: '09:00', to: '17:00' }] },
    tuesday: { enabled: true, timeRanges: [{ from: '09:00', to: '17:00' }] },
    wednesday: { enabled: true, timeRanges: [{ from: '09:00', to: '17:00' }] },
    thursday: { enabled: true, timeRanges: [{ from: '09:00', to: '17:00' }] },
    friday: { enabled: true, timeRanges: [{ from: '09:00', to: '17:00' }] },
    saturday: { enabled: false, timeRanges: [{ from: '09:00', to: '17:00' }] },
    sunday: { enabled: false, timeRanges: [{ from: '09:00', to: '17:00' }] },
};
/**
 * Default business hours schedule for office hours filtering
 * Used when evaluating BUSINESS_HOURS vs AFTER_HOURS conditions
 */
export const DEFAULT_WEEKLY_BUSINESS_HOURS = {
    monday: { enabled: true, timeRanges: [{ from: '09:00', to: '17:00' }] },
    tuesday: { enabled: true, timeRanges: [{ from: '09:00', to: '17:00' }] },
    wednesday: { enabled: true, timeRanges: [{ from: '09:00', to: '17:00' }] },
    thursday: { enabled: true, timeRanges: [{ from: '09:00', to: '17:00' }] },
    friday: { enabled: true, timeRanges: [{ from: '09:00', to: '17:00' }] },
    saturday: { enabled: false, timeRanges: [] },
    sunday: { enabled: false, timeRanges: [] },
};
/**
 * Default business-hours window used to clamp scheduled send times
 * (e.g. sequence follow-up calls) into a reasonable local time-of-day.
 */
export const DEFAULT_BUSINESS_HOURS_WINDOW = { startHour: 9, endHour: 18 };
/**
 * If `target` falls outside the given business-hours window in `timezoneName`,
 * moves it to the nearest window boundary (startHour or endHour) on the same
 * local calendar day, preserving the original minute so pushed-out sends
 * don't all pile up on the same clock minute.
 *
 * @example
 * // 5:05am local -> 9:05am local (same day)
 * // 10:01pm local -> 6:01pm local (same day)
 * // 2:30pm local -> unchanged (already inside the window)
 */
export function clampToBusinessHoursWindow(target, timezoneName, hoursWindow = DEFAULT_BUSINESS_HOURS_WINDOW) {
    const ianaTimezone = resolveTimezoneName(timezoneName);
    const localTarget = DateTime.fromJSDate(target, { zone: ianaTimezone });
    if (!localTarget.isValid) {
        return target;
    }
    const startMinutes = hoursWindow.startHour * 60;
    const endMinutes = hoursWindow.endHour * 60;
    const minutesOfDay = localTarget.hour * 60 + localTarget.minute;
    if (minutesOfDay >= startMinutes && minutesOfDay < endMinutes) {
        return target;
    }
    const boundaryHour = minutesOfDay < startMinutes ? hoursWindow.startHour : hoursWindow.endHour;
    return localTarget
        .set({ hour: boundaryHour, second: 0, millisecond: 0 })
        .toJSDate();
}
/**
 * Resolves a timezone to a valid IANA timezone with an Eastern fallback.
 * - Supports enum-style values (e.g., AMERICA_NEW_YORK)
 * - Supports IANA values (e.g., America/New_York)
 * - Falls back to Eastern when missing/invalid
 */
export function resolveTimezoneName(timezoneName) {
    const trimmedTimezoneName = timezoneName?.trim();
    if (!trimmedTimezoneName) {
        return DEFAULT_TIMEZONE_NAME;
    }
    const normalizedTimezone = toIANATimezone(trimmedTimezoneName);
    return normalizedTimezone || DEFAULT_TIMEZONE_NAME;
}
/**
 * Utility functions for office hours validation
 * Shared between frontend and backend for consistent business logic
 */
export class OfficeHoursValidator {
    constructor() {
        // Interface compliance (instance methods delegate to static methods)
        this.hasAtLeastOneEnabledDay = OfficeHoursValidator.hasAtLeastOneEnabledDay;
        this.enabledDaysHaveTimeRanges = OfficeHoursValidator.enabledDaysHaveTimeRanges;
        this.validateMaxTimeRangesPerDay = OfficeHoursValidator.validateMaxTimeRangesPerDay;
        this.hasTimeRangeOverlap = OfficeHoursValidator.hasTimeRangeOverlap;
        this.isWithinBusinessHours = OfficeHoursValidator.isWithinBusinessHours;
    }
    /**
     * Validates that at least one day is enabled
     */
    static hasAtLeastOneEnabledDay(schedule) {
        const days = [
            'monday',
            'tuesday',
            'wednesday',
            'thursday',
            'friday',
            'saturday',
            'sunday',
        ];
        return days.some(day => schedule[day]?.enabled === true);
    }
    /**
     * Validates that enabled days have at least one time range
     */
    static enabledDaysHaveTimeRanges(schedule) {
        const days = [
            'monday',
            'tuesday',
            'wednesday',
            'thursday',
            'friday',
            'saturday',
            'sunday',
        ];
        for (const day of days) {
            const daySchedule = schedule[day];
            if (daySchedule?.enabled &&
                (!daySchedule.timeRanges || daySchedule.timeRanges.length === 0)) {
                return false;
            }
        }
        return true;
    }
    /**
     * Validates maximum time ranges per day (currently 2)
     */
    static validateMaxTimeRangesPerDay(schedule) {
        const days = [
            'monday',
            'tuesday',
            'wednesday',
            'thursday',
            'friday',
            'saturday',
            'sunday',
        ];
        for (const day of days) {
            const daySchedule = schedule[day];
            if (daySchedule?.timeRanges && daySchedule.timeRanges.length > 2) {
                return false;
            }
        }
        return true;
    }
    /**
     * Checks if time ranges overlap within a day
     */
    static hasTimeRangeOverlap(timeRanges) {
        if (timeRanges.length <= 1)
            return false;
        for (let i = 0; i < timeRanges.length; i++) {
            for (let j = i + 1; j < timeRanges.length; j++) {
                const range1 = timeRanges[i];
                const range2 = timeRanges[j];
                const start1 = this.timeToMinutes(range1.from);
                const end1 = this.timeToMinutes(range1.to);
                const start2 = this.timeToMinutes(range2.from);
                const end2 = this.timeToMinutes(range2.to);
                // Check for overlap: start1 < end2 && start2 < end1
                if (start1 < end2 && start2 < end1) {
                    return true;
                }
            }
        }
        return false;
    }
    /**
     * Validates a single time range (start time before end time)
     * Supports 24:00 as a valid end time (end of day)
     */
    static isValidTimeRange(range) {
        const fromMinutes = this.timeToMinutes(range.from);
        const toMinutes = this.timeToMinutes(range.to);
        return fromMinutes < toMinutes && toMinutes <= 24 * 60;
    }
    /**
     * Validates time format (HH:MM) - supports 00:00 to 24:00
     * Note: 24:00 is only valid as 24:00, not 24:15, 24:30, etc.
     */
    static isValidTimeFormat(time) {
        // Match HH:MM format where HH is 00-23 with any minutes, or 24:00 exactly
        const normalTimePattern = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
        const endOfDayPattern = /^24:00$/;
        return normalTimePattern.test(time) || endOfDayPattern.test(time);
    }
    /**
     * Converts time string to minutes for comparison
     */
    static timeToMinutes(time) {
        const [hours, minutes] = time.split(':').map(Number);
        return hours * 60 + minutes;
    }
    /**
     * Evaluates if a given time falls within business hours
     * @param timeISOString ISO string of the time to check
     * @param timezoneName Timezone name (supports both formats: America/New_York or AMERICA_NEW_YORK)
     * @param weeklySchedule Weekly schedule to check against
     * @returns true if within business hours, false otherwise
     */
    static isWithinBusinessHours(timeISOString, timezoneName, weeklySchedule) {
        try {
            const normalizedTimezone = toIANATimezone(timezoneName);
            if (!normalizedTimezone) {
                return false;
            }
            const callTime = new Date(timeISOString);
            const formatter = new Intl.DateTimeFormat('en-US', {
                timeZone: normalizedTimezone,
                weekday: 'long',
                hour: '2-digit',
                minute: '2-digit',
                hour12: false,
            });
            const parts = formatter.formatToParts(callTime);
            const dayName = parts
                .find(part => part.type === 'weekday')
                ?.value.toLowerCase();
            const hour = parts.find(part => part.type === 'hour')?.value;
            const minute = parts.find(part => part.type === 'minute')?.value;
            if (!dayName || !hour || !minute) {
                return false;
            }
            const currentTimeMinutes = parseInt(hour) * 60 + parseInt(minute);
            const daySchedule = weeklySchedule[dayName];
            if (!daySchedule?.enabled || !daySchedule.timeRanges?.length) {
                return false;
            }
            return daySchedule.timeRanges.some((range) => {
                const startMinutes = this.timeToMinutes(range.from);
                const endMinutes = this.timeToMinutes(range.to);
                return (currentTimeMinutes >= startMinutes && currentTimeMinutes < endMinutes);
            });
        }
        catch (error) {
            return false;
        }
    }
}
/**
 * Utility functions for transforming between frontend and TwentyCRM formats
 * Shared between frontend and backend for consistent data transformation
 * // TODO: This is a temporary solution to transform the data between the frontend and backend.
 * // We may be able to remove
 */
export class OfficeHoursTransformer {
    constructor() {
        // Interface compliance (instance methods delegate to static methods)
        this.toTwentyCRMFormat = OfficeHoursTransformer.toTwentyCRMFormat;
        this.fromTwentyCRMFormat = OfficeHoursTransformer.fromTwentyCRMFormat;
    }
    /**
     * Transforms frontend schedule format to TwentyCRM format
     */
    static toTwentyCRMFormat(frontendSchedule) {
        const days = [
            'monday',
            'tuesday',
            'wednesday',
            'thursday',
            'friday',
            'saturday',
            'sunday',
        ];
        const twentySchedule = {};
        for (const day of days) {
            const daySchedule = frontendSchedule[day];
            if (!daySchedule ||
                !daySchedule.enabled ||
                !daySchedule.timeRanges ||
                daySchedule.timeRanges.length === 0) {
                // Day is disabled or no time ranges
                twentySchedule[day] = [{ allDay: false }];
            }
            else {
                // Transform each time range to TwentyCRM format
                twentySchedule[day] = daySchedule.timeRanges.map((range) => ({
                    allDay: false,
                    startTime: range.from,
                    endTime: range.to,
                }));
            }
        }
        return twentySchedule;
    }
    /**
     * Transforms TwentyCRM format to frontend format
     */
    static fromTwentyCRMFormat(twentySchedule) {
        const days = [
            'monday',
            'tuesday',
            'wednesday',
            'thursday',
            'friday',
            'saturday',
            'sunday',
        ];
        const frontendSchedule = {};
        for (const day of days) {
            const daySchedule = twentySchedule[day];
            if (!daySchedule ||
                daySchedule.length === 0 ||
                (daySchedule.length === 1 &&
                    daySchedule[0].allDay === false &&
                    !daySchedule[0].startTime)) {
                // Day is disabled
                frontendSchedule[day] = {
                    enabled: false,
                    timeRanges: [{ from: '09:00', to: '17:00' }], // Default range
                };
            }
            else {
                // Transform TwentyCRM ranges to frontend format
                const timeRanges = daySchedule
                    .filter(range => range.startTime && range.endTime)
                    .map(range => ({
                    from: range.startTime,
                    to: range.endTime,
                }));
                frontendSchedule[day] = {
                    enabled: timeRanges.length > 0,
                    timeRanges: timeRanges.length > 0
                        ? timeRanges
                        : [{ from: '09:00', to: '17:00' }],
                };
            }
        }
        return frontendSchedule;
    }
}
//# sourceMappingURL=office-hours.js.map